import React, { useState, useMemo, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import useEventos, { parseDesc as parseDescAgenda, TODAS_LAS_UNIDADES } from '../hooks/useEventos';
import { val } from '../utils/surgeryHelpers';
import { createCalendarLog } from '../objects/calendarLog';
import SurgeryCard   from '../components/SurgeryCard';
import SurgeryTable  from '../components/SurgeryTable';
import SurgeryDetailModal from '../components/SurgeryDetailModal';
import SurgeryEditModal   from '../components/SurgeryEditModal';
import SurgeryTimesModal  from '../components/SurgeryTimesModal';
import EnCirugiaBanner    from '../components/EnCirugiaBanner';
import AppFooter          from '../components/AppFooter';
import ThemeToggle        from '../components/ThemeToggle';
import useTheme           from '../hooks/useTheme';
import NuevaReservaModal  from '../components/NuevaReservaModal';
import CreateUserModal    from '../components/CreateUserModal';
import UsersPanel         from '../components/UsersPanel';
import { obtenerPermisos } from '../utils/perfiles';
import AgendaView    from '../components/AgendaView';
import DashboardPage from '../views/DashboardPage';
import ExportButton  from '../components/ExportButton';
import macLogo       from '../styles/img/logo.png';
import '../styles/global.css';
import { normISO } from '../utils/surgeryHelpers';


/* ╔══════════════════════════════════════════════════════════╗
   ║  MODO PRUEBA — Confirmar                                  ║
   ║  Mientras esté en `true`, el botón "Confirmar" NO escribe ║
   ║  nada en Firestore: solo muestra en consola el payload    ║
   ║  que se habría enviado y un aviso confirmando la          ║
   ║  solicitud. Poner en `false` para volver al guardado real.║
   ╚══════════════════════════════════════════════════════════╝ */
const MODO_PRUEBA_CONFIRMAR = false;

/* ── Helpers ──────────────────────────────────────────────── */
// Fecha LOCAL (AAAA-MM-DD). Con toISOString() (UTC) después de las 18:00
// en México ya salía el día siguiente.
const fechaLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hoy = () => fechaLocal(new Date());

const enUnaSemana = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return fechaLocal(d);
};

const fmtFechaCorta = (iso) => {
  if (!iso) return '';
  return new Date(iso + 'T00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
};

/* ── Componente principal ─────────────────────────────────── */
const AgendaPage = ({
  userName,
  userNomHospital,
  userCalendar,
  userLevel,
  isCorporativo,
  uhList,
  setuserNomHospital,
  setUserCalendar,
  onLogout,
}) => {
  const { esOscuro, toggleTheme } = useTheme();
  const dashboardRef = useRef(null);

  /* ── Perfiles de acceso (ver utils/perfiles.js) ─────────────
     - Agenda:        opera su unidad (reservar, confirmar, reagendar,
                      cancelar, registrar tiempos / realizado).
     - Consulta:      solo lectura de su unidad; puede descargar.
     - Mesa de Ayuda: solo lectura de TODAS las unidades (selector
                      general) + pestaña Usuarios. */
  const permisos               = obtenerPermisos(userLevel, isCorporativo);
  const mostrarSelectorGeneral = permisos.vistaGlobal;
  const soloLectura            = permisos.soloLectura;
  const gestionUsuarios        = permisos.gestionUsuarios;
  // Monitor: solo la ventana Agenda (pantallas / estaciones de trabajo)
  const soloAgenda             = permisos.soloAgenda;
  const [mostrarCrearUsuario, setMostrarCrearUsuario] = useState(false);

  // Unidades reales (con quirófanos) — se excluye la propia "Corporativo"
  const unidadesReales = (uhList || []).filter((u) => u.hospital !== 'Corporativo');

  const [tabActual,   setTabActual]   = useState('agenda');
  const [rangoDesde,  setRangoDesde]  = useState(hoy());
  const [rangoHasta,  setRangoHasta]  = useState(enUnaSemana());
  const [fechaAgenda, setFechaAgenda] = useState(hoy());
  const [busqueda,    setBusqueda]    = useState('');
  const [vista,       setVista]       = useState('tarjetas'); // 'tarjetas' | 'tabla'
  const [showFiltro,  setShowFiltro]  = useState(false);
  const [detalleDoc,  setDetalleDoc]  = useState(null);
  const [editDoc,     setEditDoc]     = useState(null);
  const [tiemposDoc,  setTiemposDoc]  = useState(null);
  const [nuevaReservaPreset, setNuevaReservaPreset] = useState(null);

  // Unidad vista en Pendientes/Confirmadas/Realizado/Canceladas/Indicadores
  // — puede ser "todas" solo para el perfil admin/soporte de Corporativo.
  const [unidadListas, setUnidadListas] = useState(
    mostrarSelectorGeneral ? TODAS_LAS_UNIDADES : userCalendar
  );
  // Unidad vista en Agenda — siempre una unidad real específica.
  // Unidad vista en Agenda — siempre una unidad real específica. Para el
  // perfil admin/soporte arranca vacía a propósito (su propia unidad es
  // "Corporativo", que no tiene quirófanos) — se autoselecciona abajo en
  // cuanto carga uhList, o el usuario la elige manualmente del selector.
  const [unidadAgenda, setUnidadAgenda] = useState(() => mostrarSelectorGeneral ? '' : userCalendar);

  // Si el perfil admin/soporte aún no tiene una unidad real seleccionada
  // para Agenda (su propio calendar es "Corporativo", sin quirófanos),
  // se autoselecciona la primera unidad real disponible en cuanto carga.
  useEffect(() => {
    if (!mostrarSelectorGeneral || unidadesReales.length === 0) return;
    const esValida = unidadesReales.some((u) => u.calendar === unidadAgenda);
    if (!esValida) setUnidadAgenda(unidadesReales[0].calendar);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uhList, mostrarSelectorGeneral]);

  const { pendientes, confirmadas, historico, canceladas, loading, error, todos } = useEventos(
    mostrarSelectorGeneral ? unidadListas : userCalendar,
    rangoDesde + 'T00:00',
    rangoHasta + 'T23:59'
  );

  // Unidad "efectiva" a usar en cada contexto (fuera del perfil admin/soporte,
  // siempre es simplemente userCalendar, como antes)
  const unidadListasEfectiva = mostrarSelectorGeneral ? unidadListas : userCalendar;
  const unidadAgendaEfectiva = mostrarSelectorGeneral ? unidadAgenda : userCalendar;

  /* ── Monitor: la vista se mantiene siempre en el día actual ──
     Revisa cada minuto; al cambiar el día (medianoche) avanza la fecha
     de la Agenda y el rango que se escucha en tiempo real. */
  useEffect(() => {
    if (!soloAgenda) return undefined;
    const revisar = () => {
      const h = hoy();
      setFechaAgenda((f) => (f !== h ? h : f));
      setRangoDesde((f) => (f !== h ? h : f));
      setRangoHasta((f) => { const s = enUnaSemana(); return f !== s ? s : f; });
      setTabActual('agenda');
    };
    revisar();
    const id = setInterval(revisar, 60000);
    return () => clearInterval(id);
  }, [soloAgenda]);

  const pantallaCompleta = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    else if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
  };

  /* ── Mapa de listas por tab ─────────────────────────────── */
  const TABS = soloAgenda ? [{ id: 'agenda', label: 'Agenda', lista: null }] : [
    { id: 'agenda',      label: 'Agenda',       lista: null        },
    { id: 'pendientes',  label: 'Pendientes',   lista: pendientes  },
    { id: 'confirmadas', label: 'Confirmadas',  lista: confirmadas },
    { id: 'historico',   label: 'Realizado',    lista: historico   },
    { id: 'canceladas',  label: 'Canceladas',   lista: canceladas  },
    { id: 'dashboard',   label: 'Indicadores',  lista: null        },
    ...(gestionUsuarios ? [{ id: 'usuarios', label: 'Usuarios', lista: null }] : []),
  ];
  // Pestañas que muestran listas de agendamientos
  const esTabLista = !['agenda', 'dashboard', 'usuarios'].includes(tabActual);

  /* ── Búsqueda en tiempo real ────────────────────────────── */
  const listaFiltrada = useMemo(() => {
    if (tabActual === 'agenda' || tabActual === 'dashboard' || tabActual === 'usuarios') return [];
    const base = TABS.find((t) => t.id === tabActual)?.lista || [];
    if (!busqueda.trim()) return base;
    const q = busqueda.toLowerCase();
    return base.filter((doc) => {
      const desc = (doc?.event?.description || '').toLowerCase();
      return desc.includes(q);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabActual, pendientes, confirmadas, historico, canceladas, busqueda]);

  // Histórico y Canceladas son de solo consulta: no se pueden editar,
  // cancelar (ya está cancelada) ni volver a marcar como Realizada.
  const esArchivado = tabActual === 'historico' || tabActual === 'canceladas';

  /* ── Handlers ────────────────────────────────────────────── */
  const handleConfirmar = async (doc) => {
    const ev = doc?.event || {};
    const fields = parseDescAgenda(ev.description);

    const result = await Swal.fire({
      icon: 'question',
      title: '¿Confirmar agendamiento?',
      text: 'Esta cirugía se marcará como Confirmada.',
      showCancelButton: true,
      confirmButtonText: 'Sí, confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#04afaa',
      cancelButtonColor: '#9ca3af',
    });
    if (!result.isConfirmed) return;

    Swal.fire({ title: 'Confirmando…', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    const eventoClonado = { ...ev, start: { ...ev.start }, end: { ...ev.end } };
    const ahora = new Date().toISOString();

    if (MODO_PRUEBA_CONFIRMAR) {
      // ── MODO PRUEBA: no se escribe en Firestore ──
      const payload = {
        eventId    : doc.id,
        peticion   : 'confirmar',
        status     : 'Confirmada',
        event      : eventoClonado,
        AppointmentType: ev.description,
        solicitado : fields['Solicitado'] || userName || '',
        create     : userName || '',
        time       : ahora,
        id         : doc.id,
      };
      console.log('[MODO PRUEBA] Payload que se enviaría a createCalendarLog:', payload);

      Swal.fire({
        icon: 'info',
        title: 'Solicitud generada (modo prueba)',
        text: 'No se guardó nada en la base de datos. Revisa la consola para ver el payload que se habría enviado.',
      });
      return;
    }

    try {
      const res = await createCalendarLog(
        doc.id,                                   // eventId
        'confirmar',                               // peticion
        'Confirmada',                               // status
        eventoClonado,                              // event
        ev.description,                             // AppointmentType
        fields['Solicitado'] || userName || '',      // solicitado
        userName || '',                              // create
        ahora,                                       // time
        doc.id                                       // id
      );

      if (res === 'success') {
        Swal.fire({ icon: 'success', title: 'Agendamiento confirmado', timer: 1600, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'No se pudo confirmar', text: 'Intenta nuevamente.' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo confirmar', text: 'Intenta nuevamente.' });
    }
  };

  const handleCancelar = async (doc) => {
    const ev = doc?.event || {};
    const fields = parseDescAgenda(ev.description);

    const MOTIVOS_CANCELACION = [
      'Cancelada por el médico',
      'Cancelada por el paciente',
      'Error de captura',
      'Expediente',
      'Hospital',
      'Aseguradora',
    ];

    const { value: motivo, isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: '¿Cancelar agendamiento?',
      text: 'Esta acción marcará la cirugía como Cancelada.',
      input: 'select',
      inputLabel: 'Motivo de cancelación',
      inputPlaceholder: 'Selecciona un motivo…',
      inputOptions: MOTIVOS_CANCELACION.reduce((acc, m) => ({ ...acc, [m]: m }), {}),
      inputValidator: (value) => (!value ? 'Debes seleccionar un motivo de cancelación.' : undefined),
      showCancelButton: true,
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'Regresar',
      confirmButtonColor: '#e12f3b',
      cancelButtonColor: '#9ca3af',
    });
    if (!isConfirmed) return;

    Swal.fire({ title: 'Cancelando…', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    const eventoClonado = { ...ev, start: { ...ev.start }, end: { ...ev.end } };
    eventoClonado.motivoCancelacion = motivo;
    const ahora = new Date().toISOString();

    try {
      const res = await createCalendarLog(
        doc.id,                                   // eventId
        'cancelar',                                 // peticion
        'Cancelada',                                // status
        eventoClonado,                              // event
        ev.description,                             // AppointmentType
        fields['Solicitado'] || userName || '',      // solicitado
        userName || '',                              // create
        ahora,                                       // time
        doc.id                                       // id
      );

      if (res === 'success') {
        Swal.fire({ icon: 'success', title: 'Agendamiento cancelado', timer: 1600, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'No se pudo cancelar', text: 'Intenta nuevamente.' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo cancelar', text: 'Intenta nuevamente.' });
    }
  };

  const handleEditar   = (doc) => setEditDoc(doc);

  const [exportandoPDF, setExportandoPDF] = useState(false);

  const handleExportarIndicadoresPDF = async () => {
    if (!dashboardRef.current?.exportarPDF) {
      Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: 'La vista de Indicadores aún no está lista.' });
      return;
    }
    const nombreUnidadListas = unidadListasEfectiva === TODAS_LAS_UNIDADES
      ? 'Todas las unidades'
      : (unidadesReales.find((u) => u.calendar === unidadListasEfectiva)?.hospital || userNomHospital);

    setExportandoPDF(true);
    try {
      await dashboardRef.current.exportarPDF({
        hospital: nombreUnidadListas,
        nombreArchivo: `indicadores_${unidadListasEfectiva}_${rangoDesde}_${rangoHasta}`,
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo generar el PDF', text: 'Intenta nuevamente.' });
    } finally {
      setExportandoPDF(false);
    }
  };

  const handleSlotClick = async (nombreSala, horaInicial, esNoProgramada) => {
    if (esNoProgramada) {
      const result = await Swal.fire({
        icon: 'warning',
        title: '¿Agendar cirugía no programada?',
        html: `Estás por agendar en un horario ya transcurrido:<br/>
               <strong>${nombreSala}</strong> · ${fechaAgenda} · ${horaInicial}<br/><br/>
               Esta cirugía se marcará como <strong>No Programada</strong>.`,
        showCancelButton: true,
        confirmButtonText: 'Sí, continuar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d97706',
        cancelButtonColor: '#9ca3af',
      });
      if (!result.isConfirmed) return;
    }

    setNuevaReservaPreset({
      unidad: userCalendar,
      sala: nombreSala,
      fecha: fechaAgenda,
      horaInicial,
      noProgramada: Boolean(esNoProgramada),
    });
  };

  const handleTiempos  = async (doc) => {
    const ev = doc?.event || {};
    const fields = parseDescAgenda(ev.description);

    const medico       = val(fields['Medico']);
    const paciente      = val(fields['Paciente']);
    const procedimiento = val(fields['Procedimiento']);

    const escapeHtml = (str) => String(str).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

    const filas = [
      medico       && `<div><strong>Médico:</strong> ${escapeHtml(medico)}</div>`,
      paciente      && `<div><strong>Paciente:</strong> ${escapeHtml(paciente)}</div>`,
      procedimiento && `<div><strong>Procedimiento:</strong> ${escapeHtml(procedimiento)}</div>`,
    ].filter(Boolean).join('');

    const result = await Swal.fire({
      icon: 'question',
      title: '¿Desea informar cirugía realizada?',
      html: `<div style="text-align:left;font-size:13px;line-height:1.6;">
              ${filas}
              <div style="margin-top:10px;color:#6b7280;">Podrás capturar el número de hospitalización y observaciones importantes.</div>
             </div>`,
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#7c3aed',
      cancelButtonColor: '#9ca3af',
    });
    if (!result.isConfirmed) return;

    setTiemposDoc(doc);
  };
  const handleDetalle  = (doc) => setDetalleDoc(doc);

  /* ── Docs a exportar según la pestaña activa ──────────────
     "agenda"    → solo el día seleccionado
     "dashboard" → hospital + rango de fechas (mismo filtro que usan
                   las gráficas de Indicadores, para que el conteo
                   del botón "Exportar" coincida con lo que se ve ahí)
     resto       → la lista ya filtrada de esa pestaña (Pendientes/
                   Confirmadas/Histórico) */
  const docsExportar = (() => {
    if (tabActual === 'agenda') {
      return todos.filter((doc) => {
        const fields = parseDescAgenda(doc?.event?.description);
        const uh = fields['Unidad Hospitalaroa'] || fields['Unidad Hospitalaria'] || '';
        if (uh !== unidadAgendaEfectiva) return false;
        const iso = doc?.event?.start?.dateTime;
        if (!iso) return false;
        return normISO(iso).split('T')[0] === fechaAgenda;
      });
    }
    if (tabActual === 'dashboard') {
      const desde = rangoDesde ? new Date(rangoDesde) : null;
      const hasta = rangoHasta ? new Date(rangoHasta + 'T23:59') : null;
      return todos.filter((doc) => {
        const fields = parseDescAgenda(doc?.event?.description);
        const uh = fields['Unidad Hospitalaroa'] || fields['Unidad Hospitalaria'] || '';
        if (unidadListasEfectiva !== TODAS_LAS_UNIDADES && uh !== unidadListasEfectiva) return false;
        const iso = normISO(doc?.event?.start?.dateTime);
        if (!iso) return false;
        const inicio = new Date(iso);
        return (!desde || inicio >= desde) && (!hasta || inicio <= hasta);
      });
    }
    return TABS.find((t) => t.id === tabActual)?.lista || [];
  })();

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--surface-sunken)' }}>

      {/* ══ HEADER FIJO ════════════════════════════════════ */}
      <header style={{
        background  : 'var(--surface)',
        borderBottom: '2px solid #04afaa',
        boxShadow   : '0 2px 8px rgba(0,0,0,.07)',
        left: 0, position: 'fixed', right: 0, top: 0, zIndex: 300,
      }}>

        {/* Barra principal */}
        <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', minHeight: '60px', padding: '0 20px', position: 'relative' }}>

          {/* Logo */}
          <img src={macLogo} alt="MAC" style={{ height: '42px', width: 'auto' }} />

          {/* Centro: hospital + usuario */}
          <div style={{ flex: 1, padding: '0 16px', textAlign: 'center' }}>
            {mostrarSelectorGeneral ? (
              tabActual === 'agenda' ? (
                // Agenda: siempre exige una unidad real específica (sin "Todas")
                <select
                  value={unidadAgenda}
                  onChange={(e) => setUnidadAgenda(e.target.value)}
                  style={{ background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700, padding: '4px 10px' }}
                >
                  {!unidadAgenda && <option value="">-- Selecciona unidad --</option>}
                  {unidadesReales.map((u) => <option key={u.id} value={u.calendar}>{u.hospital}</option>)}
                </select>
              ) : (
                // Resto de pestañas: "Todas las unidades" o una unidad puntual
                <select
                  value={unidadListas}
                  onChange={(e) => setUnidadListas(e.target.value)}
                  style={{ background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700, padding: '4px 10px' }}
                >
                  <option value={TODAS_LAS_UNIDADES}>Todas las unidades</option>
                  {unidadesReales.map((u) => <option key={u.id} value={u.calendar}>{u.hospital}</option>)}
                </select>
              )
            ) : isCorporativo ? (
              <select
                value={userNomHospital}
                onChange={(e) => {
                  const uh = uhList.find((u) => u.hospital === e.target.value);
                  setuserNomHospital(e.target.value);
                  if (uh?.calendar) setUserCalendar(uh.calendar);
                }}
                style={{ background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700, padding: '4px 10px' }}
              >
                {uhList.map((u) => <option key={u.id} value={u.hospital}>{u.hospital}</option>)}
              </select>
            ) : (
              <div style={{ fontSize: 'clamp(13px,1.8vw,16px)', fontWeight: 700, color: 'var(--text-primary)' }}>
                {userNomHospital}
              </div>
            )}
            <div style={{ alignItems: 'center', color: 'var(--text-secondary)', display: 'flex', fontSize: '12px', gap: '6px', justifyContent: 'center', marginTop: '1px' }}>
              <span>{permisos.etiqueta.toUpperCase()} · {userName}</span>
              {soloAgenda ? (
                <span
                  title="Perfil para pantallas: muestra la Agenda del día de su unidad, se actualiza sola y no permite acciones."
                  style={{ background: '#eef2ff', borderRadius: '20px', color: '#3730a3', fontSize: '10px', fontWeight: 700, padding: '2px 8px' }}
                >
                  🖥 Monitor
                </span>
              ) : soloLectura && (
                <span
                  title="Este perfil solo puede ver y descargar información; no puede confirmar, cancelar, reagendar ni registrar tiempos."
                  style={{ background: '#fffbeb', borderRadius: '20px', color: '#92400e', fontSize: '10px', fontWeight: 700, padding: '2px 8px' }}
                >
                  👁 Solo lectura
                </span>
              )}
            </div>
          </div>

          {/* Acciones */}
          <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
            {soloAgenda && (
              <button
                onClick={pantallaCompleta}
                title="Mostrar la Agenda en pantalla completa (Esc para salir)"
                style={{
                  alignItems: 'center', background: 'var(--surface)', border: '1px solid #04afaa',
                  borderRadius: '8px', color: '#039490', cursor: 'pointer', display: 'flex',
                  fontSize: '12px', fontWeight: 600, gap: '5px', padding: '6px 12px', whiteSpace: 'nowrap',
                }}
              >
                ⛶ Pantalla completa
              </button>
            )}
            {!soloAgenda && <button
              onClick={() => setShowFiltro((v) => !v)}
              style={{
                alignItems: 'center', background: showFiltro ? '#e6f9f8' : 'var(--surface)',
                border: '1px solid #04afaa', borderRadius: '8px', color: '#039490',
                cursor: 'pointer', display: 'flex', fontSize: '12px', fontWeight: 600,
                gap: '5px', padding: '6px 12px', whiteSpace: 'nowrap',
              }}
            >
              📅 {tabActual === 'agenda'
                ? new Date(fechaAgenda + 'T00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
                : `${fmtFechaCorta(rangoDesde)} — ${fmtFechaCorta(rangoHasta)}`}
            </button>}
            {tabActual !== 'usuarios' && permisos.puedeDescargar && <ExportButton
              docs          = {docsExportar}
              nombreArchivo = {`agenda_${tabActual}_${rangoDesde}_${rangoHasta}`}
              rangoDesde    = {rangoDesde}
              rangoHasta    = {rangoHasta}
            />}
            {tabActual === 'dashboard' && (
              <button
                onClick={handleExportarIndicadoresPDF}
                disabled={exportandoPDF}
                style={{
                  alignItems: 'center', background: '#fdf1f2', border: '1px solid #e12f3b',
                  borderRadius: '8px', color: '#e12f3b', cursor: exportandoPDF ? 'default' : 'pointer',
                  display: 'flex', fontSize: '12px', fontWeight: 600, gap: '5px',
                  opacity: exportandoPDF ? .6 : 1, padding: '6px 12px', whiteSpace: 'nowrap',
                }}
              >
                {exportandoPDF ? 'Generando…' : '📄 Exportar PDF'}
              </button>
            )}
            {gestionUsuarios && tabActual !== 'usuarios' && (
              <button
                onClick={() => setMostrarCrearUsuario(true)}
                title="Crear un nuevo usuario del sistema"
                style={{
                  alignItems: 'center', background: '#f3ecfe', border: '1px solid #7c3aed',
                  borderRadius: '8px', color: '#7c3aed', cursor: 'pointer',
                  display: 'flex', fontSize: '12px', fontWeight: 600, gap: '5px',
                  padding: '6px 12px', whiteSpace: 'nowrap',
                }}
              >
                👤 Nuevo usuario
              </button>
            )}
            <ThemeToggle esOscuro={esOscuro} onToggle={toggleTheme} />
            <button
              onClick={onLogout}
              style={{
                background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '8px',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: '6px 14px',
              }}
            >
              Salir
            </button>
          </div>
        </div>

        {/* Selector de rango / fecha */}
        {showFiltro && !soloAgenda && (
          <div style={{ alignItems: 'center', borderTop: '1px solid var(--border-secondary)', display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '10px 20px' }}>
            {tabActual === 'agenda' ? (
              <>
                <label style={{ alignItems: 'center', display: 'flex', fontSize: '12px', fontWeight: 700, gap: '8px' }}>
                  Fecha
                  <input type="date" value={fechaAgenda}
                    onChange={(e) => setFechaAgenda(e.target.value)}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', padding: '5px 10px' }}
                  />
                </label>
              </>
            ) : (
              <>
                <label style={{ alignItems: 'center', display: 'flex', fontSize: '12px', fontWeight: 700, gap: '8px' }}>
                  Desde
                  <input type="date" value={rangoDesde}
                    onChange={(e) => setRangoDesde(e.target.value)}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', padding: '5px 10px' }}
                  />
                </label>
                <label style={{ alignItems: 'center', display: 'flex', fontSize: '12px', fontWeight: 700, gap: '8px' }}>
                  Hasta
                  <input type="date" value={rangoHasta} min={rangoDesde}
                    onChange={(e) => setRangoHasta(e.target.value)}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', padding: '5px 10px' }}
                  />
                </label>
              </>
            )}
            <button
              onClick={() => setShowFiltro(false)}
              style={{ background: '#04afaa', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 700, padding: '7px 20px' }}
            >
              Aplicar
            </button>
          </div>
        )}

        {/* Pestañas de navegación */}
        <div style={{ alignItems: 'center', borderTop: '1px solid var(--border-secondary)', display: 'flex', flexWrap: 'nowrap', gap: '4px', overflowX: 'auto', padding: '6px 16px', scrollbarWidth: 'none' }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setTabActual(tab.id); setBusqueda(''); }}
              style={{
                alignItems   : 'center',
                background   : tabActual === tab.id ? '#e6f9f8' : 'transparent',
                border       : tabActual === tab.id ? '1.5px solid #04afaa' : '1px solid var(--border-primary)',
                borderRadius : '8px',
                color        : tabActual === tab.id ? '#039490' : 'var(--text-secondary)',
                cursor       : 'pointer',
                display      : 'inline-flex',
                flexShrink   : 0,
                fontSize     : '12px',
                fontWeight   : tabActual === tab.id ? 700 : 500,
                gap          : '6px',
                padding      : '6px 12px',
                whiteSpace   : 'nowrap',
              }}
            >
              {tab.label}
              <span style={{
                background  : tabActual === tab.id ? '#039490' : 'var(--border-primary)',
                borderRadius: '20px', color: '#fff',
                fontSize: '10px', fontWeight: 700, padding: '1px 6px',
              }}>
                {tab.lista != null ? tab.lista.length : ''}
              </span>
            </button>
          ))}

          {/* Búsqueda + selector de vista — solo en tabs de lista */}
          {esTabLista && (
            <div style={{ alignItems: 'center', display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <input
                type="search"
                placeholder="Buscar médico, procedimiento…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                style={{
                  background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '12px',
                  outline: 'none', padding: '5px 12px', width: '240px', flexShrink: 0,
                }}
              />
              <div style={{ background: 'var(--surface-sunken)', borderRadius: '8px', display: 'flex', flexShrink: 0, gap: '2px', padding: '2px' }}>
                <button
                  onClick={() => setVista('tarjetas')}
                  title="Vista de tarjetas"
                  style={{
                    background: vista === 'tarjetas' ? 'var(--surface)' : 'transparent',
                    border: 'none', borderRadius: '6px', color: vista === 'tarjetas' ? '#039490' : 'var(--text-secondary)',
                    boxShadow: vista === 'tarjetas' ? '0 1px 2px rgba(0,0,0,.1)' : 'none',
                    cursor: 'pointer', fontSize: '13px', fontWeight: 700, padding: '5px 10px',
                  }}
                >
                  ▤
                </button>
                <button
                  onClick={() => setVista('tabla')}
                  title="Vista de tabla"
                  style={{
                    background: vista === 'tabla' ? 'var(--surface)' : 'transparent',
                    border: 'none', borderRadius: '6px', color: vista === 'tabla' ? '#039490' : 'var(--text-secondary)',
                    boxShadow: vista === 'tabla' ? '0 1px 2px rgba(0,0,0,.1)' : 'none',
                    cursor: 'pointer', fontSize: '13px', fontWeight: 700, padding: '5px 10px',
                  }}
                >
                  ☰
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ══ CONTENIDO ═════════════════════════════════════ */}
      <main style={{ flex: 1, paddingTop: showFiltro ? '186px' : '130px' }}>
        <div style={{ padding: '16px 20px 80px' }}>

          {/* Agenda sin unidad seleccionada (solo perfil admin/soporte) —
              evita mostrar la grilla "cargando" indefinidamente sin motivo
              aparente para el usuario */}
          {tabActual === 'agenda' && !unidadAgendaEfectiva && (
            <div style={{
              alignItems: 'center', background: 'var(--surface)', border: '1px dashed var(--border-primary)',
              borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '10px',
              padding: '48px 20px', textAlign: 'center',
            }}>
              <span style={{ fontSize: '28px' }}>🏥</span>
              <span style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>
                Selecciona una unidad para ver su agenda
              </span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '12px', maxWidth: '360px' }}>
                Usa el selector de unidad en la parte superior — la Agenda del día siempre necesita
                una unidad hospitalaria específica (no está disponible la vista "Todas las unidades").
              </span>
            </div>
          )}

          {/* Cirugías en curso (banner en vivo) */}
          {tabActual === 'agenda' && !loading && unidadAgendaEfectiva && (
            <EnCirugiaBanner
              docs={todos.filter((doc) => {
                const f = parseDescAgenda(doc?.event?.description);
                const uh = f['Unidad Hospitalaroa'] || f['Unidad Hospitalaria'] || '';
                return uh === unidadAgendaEfectiva;
              })}
              onAbrirTiempos={soloLectura ? undefined : handleTiempos}
            />
          )}

          {/* Vista de agenda (grilla horaria) */}
          {tabActual === 'agenda' && !loading && unidadAgendaEfectiva && (() => {
            const eventosDelDia = todos.filter((doc) => {
              const fields = parseDescAgenda(doc?.event?.description);
              const uh = fields['Unidad Hospitalaroa'] || fields['Unidad Hospitalaria'] || '';
              if (uh !== unidadAgendaEfectiva) return false;
              const iso = doc?.event?.start?.dateTime;
              if (!iso) return false;
              // Se incluyen también las cirugías que empezaron el día anterior y
              // terminan en este día (cruzan medianoche).
              const ini = new Date(normISO(iso));
              const fin = new Date(normISO(doc?.event?.end?.dateTime));
              const diaIni = new Date(fechaAgenda + 'T00:00');
              const diaFin = new Date(diaIni.getTime() + 24 * 60 * 60000);
              if (!isNaN(ini) && !isNaN(fin) && fin > ini) return ini < diaFin && fin > diaIni;
              return normISO(iso).split('T')[0] === fechaAgenda;
            });
            const totalDia      = eventosDelDia.length;
            const pendientesDia = eventosDelDia.filter(d=>(d?.event?.summary||'').toLowerCase()==='pending').length;
            const confirmadasDia= eventosDelDia.filter(d=>(d?.event?.summary||'').toLowerCase()==='confirmada').length;
            const canceladasDia = eventosDelDia.filter(d=>(d?.event?.summary||'').toLowerCase()==='cancelada').length;
            return (
              <>
                {/* Resumen del día */}
                <div style={{ display:'flex', flexWrap:'wrap', gap:'10px', marginBottom:'16px', alignItems:'center' }}>
                  <span style={{ fontSize:'13px', fontWeight:500, color:'var(--color-text-secondary)' }}>
                    {new Date(fechaAgenda+'T00:00').toLocaleDateString('es-MX',{weekday:'long',day:'2-digit',month:'long',year:'numeric'})}
                  </span>
                  <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                    {[
                      { label:`${totalDia} total`,      color:'var(--text-primary)', bg:'var(--surface-sunken)' },
                      { label:`${pendientesDia} pend.`, color:'#038f8a', bg:'#e6f9f8' },
                      { label:`${confirmadasDia} conf.`,color:'#92400e', bg:'#fffbeb' },
                      { label:`${canceladasDia} canc.`, color:'#991b1b', bg:'#fdf1f2' },
                    ].map(({label,color,bg})=>(
                      <span key={label} style={{ background:bg, color, borderRadius:'20px', fontSize:'11px', fontWeight:600, padding:'3px 10px' }}>
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
                <AgendaView
                  calendar    = {unidadAgendaEfectiva}
                  eventos     = {eventosDelDia}
                  fechaISO    = {fechaAgenda}
                  onSlotClick = {soloLectura ? undefined : handleSlotClick}
                  autoActualizar = {soloAgenda}
                />
              </>
            );
          })()}

          {/* Dashboard de indicadores */}
          {tabActual === 'dashboard' && (
            <DashboardPage
              ref        = {dashboardRef}
              todos      = {todos}
              calendar   = {unidadListasEfectiva}
              rangoDesde = {rangoDesde}
              rangoHasta = {rangoHasta}
              uhList     = {uhList}
            />
          )}

          {/* Gestión de usuarios (solo Mesa de Ayuda) */}
          {tabActual === 'usuarios' && gestionUsuarios && (
            <UsersPanel
              uhList         = {uhList}
              onNuevoUsuario = {() => setMostrarCrearUsuario(true)}
            />
          )}

          {/* Descripción de sección (solo tabs de lista) */}
          {esTabLista && (
            <DescSeccion tab={tabActual} desde={rangoDesde} hasta={rangoHasta} />
          )}

          {/* Loading */}
          {loading && esTabLista && (
            <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: '12px', padding: '60px 0' }}>
              <div style={{
                animation: 'spin .8s linear infinite',
                border: '3px solid var(--border-secondary)', borderRadius: '50%',
                borderTop: '3px solid #04afaa', height: '36px', width: '36px',
              }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Cargando agendamientos…</span>
            </div>
          )}

          {/* Error */}
          {!loading && error && esTabLista && (
            <div style={{ alignItems: 'center', background: '#fdf1f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#e12f3b', display: 'flex', fontSize: '13px', fontWeight: 600, gap: '8px', maxWidth: '460px', padding: '12px 16px' }}>
              ⚠ {error}
            </div>
          )}

          {/* Sin resultados */}
          {!loading && !error && listaFiltrada.length === 0 && esTabLista && (
            <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: '8px', padding: '60px 0', textAlign: 'center' }}>
              <span style={{ fontSize: '40px' }}>📋</span>
              <span style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>Sin resultados</span>
              <span style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
                {busqueda ? `No se encontraron resultados para "${busqueda}"` : 'No hay agendamientos en esta sección'}
              </span>
            </div>
          )}

          {/* Tarjetas o tabla, según la vista seleccionada */}
          {!loading && !error && listaFiltrada.length > 0 && esTabLista && (
            vista === 'tabla' ? (
              <SurgeryTable
                docs        = {listaFiltrada}
                tipo        = {tabActual}
                onConfirmar = {(tabActual === 'pendientes' && !soloLectura) ? handleConfirmar : null}
                onCancelar  = {(!esArchivado && !soloLectura) ? handleCancelar : null}
                onEditar    = {(!esArchivado && !soloLectura) ? handleEditar : null}
                onTiempos   = {(tabActual !== 'pendientes' && !esArchivado && !soloLectura) ? handleTiempos : null}
                onDetalle   = {handleDetalle}
                mostrarUnidad = {unidadListasEfectiva === TODAS_LAS_UNIDADES}
              />
            ) : (
              <div style={{ alignItems: 'flex-start', display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
                {listaFiltrada.map((doc) => (
                  <SurgeryCard
                    key         = {doc.id}
                    doc         = {doc}
                    tipo        = {tabActual}
                    onConfirmar = {(tabActual === 'pendientes' && !soloLectura) ? handleConfirmar : null}
                    onCancelar  = {(!esArchivado && !soloLectura) ? handleCancelar : null}
                    onEditar    = {(!esArchivado && !soloLectura) ? handleEditar : null}
                    onTiempos   = {(tabActual !== 'pendientes' && !esArchivado && !soloLectura) ? handleTiempos : null}
                    onDetalle   = {handleDetalle}
                    mostrarUnidad = {unidadListasEfectiva === TODAS_LAS_UNIDADES}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </main>

      {/* ══ FOOTER ════════════════════════════════════════ */}
      <AppFooter />

      {/* ══ MODAL DE DETALLE ═══════════════════════════════ */}
      {detalleDoc && (
        <SurgeryDetailModal
          doc         = {detalleDoc}
          tipo        = {tabActual}
          onClose     = {() => setDetalleDoc(null)}
          onConfirmar = {(tabActual === 'pendientes' && !soloLectura) ? handleConfirmar : null}
          onCancelar  = {(!esArchivado && !soloLectura) ? handleCancelar : null}
          onEditar    = {(!esArchivado && !soloLectura) ? handleEditar : null}
          onTiempos   = {(tabActual !== 'pendientes' && !esArchivado && !soloLectura) ? handleTiempos : null}
          mostrarUnidad = {unidadListasEfectiva === TODAS_LAS_UNIDADES}
        />
      )}

      {/* ══ MODAL DE EDICIÓN ═══════════════════════════════ */}
      {editDoc && (
        <SurgeryEditModal
          doc      = {editDoc}
          userName = {userName}
          eventos  = {todos}
          onClose  = {() => setEditDoc(null)}
        />
      )}

      {/* ══ MODAL DE TIEMPOS QUIRÚRGICOS ════════════════════ */}
      {tiemposDoc && (
        <SurgeryTimesModal
          doc      = {tiemposDoc}
          userName = {userName}
          onClose  = {() => setTiemposDoc(null)}
        />
      )}

      {/* ══ MODAL DE NUEVA RESERVA (desde espacio disponible) ══ */}
      {nuevaReservaPreset && (
        <NuevaReservaModal
          preset  = {nuevaReservaPreset}
          onClose = {() => setNuevaReservaPreset(null)}
        />
      )}

      {/* ══ MODAL DE NUEVO USUARIO (solo perfil Mesa de Ayuda) ══ */}
      {mostrarCrearUsuario && (
        <CreateUserModal
          uhList    = {uhList}
          onClose   = {() => setMostrarCrearUsuario(false)}
          onCreated = {() => {}}
        />
      )}

    </div>
  );
};

/* ── Descripción de cada sección ──────────────────────────── */
const DescSeccion = ({ tab, desde, hasta }) => {
  const fmt = (iso) => iso
    ? new Date(iso + 'T00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
    : '';

  const INFO = {
    pendientes : { titulo: `Pendientes de confirmación · desde ${fmt(desde)}`, color: '#039490', desc: 'Agendamientos solicitados que aún no han sido confirmados, desde la fecha inicial seleccionada en adelante.' },
    confirmadas: { titulo: `Confirmadas · ${fmt(desde)} al ${fmt(hasta)}`, color: '#d97706', desc: 'Cirugías confirmadas dentro del rango de fechas seleccionado.' },
    historico  : { titulo: `Realizado · ${fmt(desde)} al ${fmt(hasta)}`,   color: 'var(--text-secondary)', desc: 'Cirugías marcadas como Realizada dentro del rango de fechas seleccionado.' },
    canceladas : { titulo: `Canceladas · ${fmt(desde)} al ${fmt(hasta)}`,  color: '#e12f3b', desc: 'Cirugías canceladas dentro del rango de fechas seleccionado.' },
  };

  const { titulo, color, desc } = INFO[tab] || INFO.pendientes;

  return (
    <div style={{ marginBottom: '16px' }}>
      <h2 style={{ color, fontSize: '15px', fontWeight: 700, margin: '0 0 3px' }}>{titulo}</h2>
      <p  style={{ color: 'var(--text-secondary)', fontSize: '12px', margin: 0 }}>{desc}</p>
    </div>
  );
};

export default AgendaPage;